import { useApiClient } from "./client";
import { API_URL } from "../constants";

const useSessionApiClient = () => {
  const apiClient = useApiClient();


  const createNewSession = async (newSessionParams) => {
    return await apiClient.post(`${API_URL}/sessions`, newSessionParams);
  };

  const getSession = async (id) => {
    const session = await apiClient.get(`${API_URL}/sessions/${id}`);

    return session;
  };

  const getSubjectNamesFromSessionId = (
    sessionId,
    subjectReviewStatusFilter,
  ) => {

    return apiClient.get(
      `${API_URL}/sessions/${sessionId}/subjects?review_status=${subjectReviewStatusFilter}`,
      null
    );
  };

  const getSubject = (
    sessionId,
    subjectName,
  ) => {
    return apiClient.get(
      `${API_URL}/sessions/${sessionId}/subjects/${subjectName}`
    );
  };

  const getSubjectImages = (sessionId, subjectName, image_states = []) => {
    let url = `${API_URL}/sessions/${sessionId}/subjects/${subjectName}/images`;
    if (image_states.length !== 0) {
      url = url.concat("?image_states=");
      for (let i = 0; i < image_states.length; ++i) {
        if (i === 0) {
          url = url.concat(image_states[i]);
        } else {
          url = url.concat(",", image_states[i]);
        }
      }
    }
    return apiClient.get(url, null);
  };

  const updateImageState = (sessionId, imageId, newState) => {
    return apiClient.patch(
      `${API_URL}/sessions/${sessionId}/images/${imageId}`,
      { ImageState: newState }
    );
  };

  const updateSubjectReviewStatus = (sessionId, subjectName, newStatus) => {
    return apiClient.patch(
      `${API_URL}/sessions/${sessionId}/subjects/${subjectName}`,
      {ReviewStatus: newStatus}
    )
  }

  const getMergeProposalsWithSubjectNamesFromCurrentSession = async (
    sessionId,
    reviewStatus
  ) => {
    const proposals = await apiClient.get(
      `${API_URL}/sessions/${sessionId}/merge?review_status=${reviewStatus}`,
      null
    );

    return await Promise.all(
      proposals.map(async (proposal) => {
        let subjectNames = getMergeProposalSubjectNames(sessionId, proposal.id);
        return {
          ...proposal,
          subjectNames: await subjectNames,
        };
      })
    );
  };

  const getMergeProposalSubjectNames = async (sessionId, mergeProposalId) => {
    let subjects = await apiClient.get(
      `${API_URL}/sessions/${sessionId}/merge/${mergeProposalId}/subjects`
    );
    return subjects.map(async (subject) => {
      return subject.name;
    });
  };

  const getMergeProposalSubjectImages = async (sessionId, mergeProposalId) => {
    const mergeSubjects = await apiClient.get(
      `${API_URL}/sessions/${sessionId}/merge/${mergeProposalId}/subjects`
    );

    return await Promise.all(
      mergeSubjects.map(async (subject) => {
        return {
          mergeProposalId,
          subjectName: subject.name,
          mergeState: subject.mergeState,
          images: await getSubjectImages(sessionId, subject.name, ["clean"]),
        };
      })
    );
  };

  const updateMergeProposalSubjectState = async (
    sessionId,
    mergeProposalSubject,
    newMergeState
  ) => {
    return await apiClient.patch(
      `${API_URL}/sessions/${sessionId}/merge/${mergeProposalSubject.mergeProposalId}/subjects/${mergeProposalSubject.subjectName}`,
      { status: newMergeState }
    );
  };

  const acceptMergeProposal = async (sessionId, mergeProposalId) => {
    return await apiClient.post(
      `${API_URL}/sessions/${sessionId}/merge/${mergeProposalId}/accept`,
      null
    );
  };

  const exportParquet = (sessionId, outputParquetFile) => {
    return apiClient.post(`${API_URL}/sessions/${sessionId}/export`, {
      OutputParquetFile: outputParquetFile,
    });
  };

  const getExports = async (sessionId) => {
    return await apiClient.get(
      `${API_URL}/sessions/${sessionId}/exports`,
      null
    );
  };

  const getSessions = async () => {
    return await apiClient.get(`${API_URL}/sessions`, null);
  };

  return {
    createNewSession,
    getSubjectNamesFromSessionId,
    getSubject,
    getSubjectImages,
    getSession,
    updateImageState,
    getMergeProposalsWithSubjectNamesFromCurrentSession,
    getMergeProposalSubjectImages,
    updateMergeProposalSubjectState,
    acceptMergeProposal,
    exportParquet,
    getExports,
    getSessions,
    updateSubjectReviewStatus,
  };
};

export { useSessionApiClient };
